import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as stylex from '@stylexjs/stylex';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { updateDocumentTitle, updateDocumentLanguage } from '../../utils/updateDocumentTitle';

// Inline static tokens for StyleX compatibility
const space = {
    0: '0',
    1: '4px',
    2: '8px',
    3: '12px',
    4: '16px',
    5: '20px',
    6: '24px',
    7: '28px',
    8: '32px',
    9: '36px',
    10: '40px',
    11: '44px',
    12: '48px',
    13: '52px',
    14: '56px',
    15: '60px',
    16: '64px',
    17: '68px',
    18: '72px',
    19: '76px',
    20: '80px',
};

// Define flag SVG components for better organization
const UKFlag = () => (
    <svg width="20" height="14" viewBox="0 0 60 30" xmlns="http://www.w3.org/2000/svg">
        <clipPath id="a"><path d="M0 0v30h60V0z" /></clipPath>
        <clipPath id="b"><path d="M30 15h30v15zv15H0zH0V0zV0h30z" /></clipPath>
        <g clipPath="url(#a)">
            <path d="M0 0v30h60V0z" fill="#012169" />
            <path d="M0 0l60 30m0-30L0 30" stroke="#fff" strokeWidth="6" />
            <path d="M0 0l60 30m0-30L0 30" clipPath="url(#b)" stroke="#C8102E" strokeWidth="4" />
            <path d="M30 0v30M0 15h60" stroke="#fff" strokeWidth="10" />
            <path d="M30 0v30M0 15h60" stroke="#C8102E" strokeWidth="6" />
        </g>
    </svg>
);

const PolandFlag = () => (
    <svg width="20" height="14" viewBox="0 0 16 10" xmlns="http://www.w3.org/2000/svg">
        <path fill="#fff" d="M0 0h16v10H0z" />
        <path fill="#dc143c" d="M0 5h16v5H0z" />
    </svg>
);

const FranceFlag = () => (
    <svg width="20" height="14" viewBox="0 0 3 2" xmlns="http://www.w3.org/2000/svg">
        <path fill="#002395" d="M0 0h1v2H0z" />
        <path fill="#fff" d="M1 0h1v2H1z" />
        <path fill="#ED2939" d="M2 0h1v2H2z" />
    </svg>
);

// Only styling what's needed to customize the Radix UI components
const styles = stylex.create({
    container: {
        position: 'fixed',
        top: space[5],
        right: space[5],
        zIndex: 100,
        transition: 'all 0.3s ease',
        '@media (max-width: 768px)': {
            bottom: space[5],
            top: 'auto',
            right: space[5],
        },
    },
    globeIcon: {
        width: '20px',
        height: '20px',
        strokeWidth: 1.8,
    },
    flagWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '24px',
        height: '16px',
        borderRadius: '3px',
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
        marginRight: space[2],
    },
    languageCode: {
        fontSize: '0.8rem',
        opacity: 0.7,
        color: 'rgba(255, 255, 255, 0.6)',
        marginLeft: 'auto',
    },
    triggerButton: {
        backgroundColor: 'rgba(20, 20, 20, 0.75)',
        color: 'rgba(255, 255, 255, 0.9)',
        border: 'none',
        borderRadius: '50%',
        width: '42px',
        height: '42px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        backdropFilter: 'blur(8px)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
        transition: 'all 0.2s ease',
        ':hover': {
            backgroundColor: 'rgba(30, 30, 30, 0.8)',
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 120, 50, 0.3)',
        },
    },
    activeButton: {
        backgroundColor: 'rgba(35, 35, 35, 0.9)',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3), 0 0 0 2px rgba(255, 120, 50, 0.5)',
        color: 'rgba(255, 150, 70, 1)',
    },
    menuItem: {
        display: 'flex',
        alignItems: 'center',
        padding: `${space[3]} ${space[4]}`,
        borderRadius: '8px',
        cursor: 'pointer',
        outline: 'none',
        backgroundColor: 'transparent',
        color: 'rgba(255, 255, 255, 0.8)',
        fontWeight: 400,
    },
    menuItemSelected: {
        backgroundColor: 'rgba(45, 45, 45, 0.6)',
        color: 'rgba(255, 150, 70, 1)',
        fontWeight: 500,
    },
    dropdownContent: {
        backgroundColor: 'rgba(25, 25, 25, 0.9)',
        backdropFilter: 'blur(10px)',
        minWidth: '160px',
        borderRadius: '12px',
        padding: space[3],
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05)',
    },
    dropdownArrow: {
        fill: 'rgba(25, 25, 25, 0.9)',
    }
});

export const LanguageSwitcher = () => {
    const { i18n, t } = useTranslation();
    const [currentLang, setCurrentLang] = useState(i18n.language || 'en');
    const [isOpen, setIsOpen] = useState(false);

    // Update state when language changes
    useEffect(() => {
        setCurrentLang(i18n.language);
        // Update document title and language attribute
        updateDocumentTitle();
        updateDocumentLanguage();
    }, [i18n.language]);

    // Handle language change
    const changeLanguage = (lang: string) => {
        i18n.changeLanguage(lang);
        localStorage.setItem('language', lang);
        setIsOpen(false);
    };

    return (
        <div {...stylex.props(styles.container)}>
            <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
                <DropdownMenu.Trigger asChild>
                    <button
                        {...stylex.props(styles.triggerButton, isOpen && styles.activeButton)}
                        aria-label={t('language.select')}
                    >
                        <svg
                            {...stylex.props(styles.globeIcon)}
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="2" y1="12" x2="22" y2="12"></line>
                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                        </svg>
                    </button>
                </DropdownMenu.Trigger>

                <DropdownMenu.Portal>
                    <DropdownMenu.Content
                        sideOffset={10}
                        align="end"
                        {...stylex.props(styles.dropdownContent)}
                    >
                        <DropdownMenu.Item
                            onSelect={() => changeLanguage('en')}
                            className={stylex.props(
                                styles.menuItem,
                                currentLang === 'en' && styles.menuItemSelected
                            ).toString()}
                        >
                            <div {...stylex.props(styles.flagWrapper)}>
                                <UKFlag />
                            </div>
                            <span>{t('language.en')}</span>
                            <span {...stylex.props(styles.languageCode)}>EN</span>
                        </DropdownMenu.Item>

                        <DropdownMenu.Item
                            onSelect={() => changeLanguage('pl')}
                            className={stylex.props(
                                styles.menuItem,
                                currentLang === 'pl' && styles.menuItemSelected
                            ).toString()}
                        >
                            <div {...stylex.props(styles.flagWrapper)}>
                                <PolandFlag />
                            </div>
                            <span>{t('language.pl')}</span>
                            <span {...stylex.props(styles.languageCode)}>PL</span>
                        </DropdownMenu.Item>

                        <DropdownMenu.Item
                            onSelect={() => changeLanguage('fr')}
                            className={stylex.props(
                                styles.menuItem,
                                currentLang === 'fr' && styles.menuItemSelected
                            ).toString()}
                        >
                            <div {...stylex.props(styles.flagWrapper)}>
                                <FranceFlag />
                            </div>
                            <span>{t('language.fr')}</span>
                            <span {...stylex.props(styles.languageCode)}>FR</span>
                        </DropdownMenu.Item>

                        <DropdownMenu.Arrow {...stylex.props(styles.dropdownArrow)} />
                    </DropdownMenu.Content>
                </DropdownMenu.Portal>
            </DropdownMenu.Root>
        </div>
    );
};

export default LanguageSwitcher; 